<template>
  <v-dialog v-model="dialog" max-width="1000">
    <v-card>
      <v-card-title class="headline">
        Actualizar Cuotas de Miembros
      </v-card-title>
      
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-row>
          <v-col cols="12">
            <v-select
              :items="blockOptions"
              label="Bloque"
              v-model="selectedBlock"
              @change="calculateQuoteSummary"
              required
              outlined
              dense
              color="primary"
              item-color="primary"
            ></v-select>
          </v-col>

          <v-col cols="12">
            <v-select
              :items="categoryOptions"
              label="Categoría"
              v-model="selectedCategory"
              @change="calculateQuoteSummary"
              multiple
              outlined
              dense
              color="primary"
              item-color="primary"
            ></v-select>
          </v-col>

          <v-col cols="12">
            <v-select
              :items="statusOptions"
              label="Status de Miembro"
              v-model="selectedStatus"
              @change="calculateQuoteSummary"
              multiple
              outlined
              dense
              color="primary"
              item-color="primary"
            ></v-select>
          </v-col>

          <v-col cols="12" v-if="baseQuote && selectedBlock">
            <v-alert
              type="info"
              dense
              text
              border="left"
              color="primary"
              class="mb-2"
            >
              <span class="text-body-2">
                Se actualizará la cuota base de <strong>${{ baseQuote }}</strong> para el bloque <strong>{{ getBlockText(selectedBlock) }}</strong>
              </span>
            </v-alert>
          </v-col>

          <v-col cols="12">
            <v-text-field
              label="Cuota Base"
              v-model="baseQuote"
              type="number"
              step="0.01"
              min="0"
              prefix="$"
              outlined
              required
              dense
              color="primary"
              @input="calculateQuoteSummary"
              :rules="[v => !!v || 'La cuota base es requerida', v => v > 0 || 'La cuota base debe ser mayor a 0']"
              hint="Esta será la cuota base para calcular todas las categorías"
              persistent-hint
            ></v-text-field>
          </v-col>
            </v-row>
          </v-col>

          <!-- Panel de Resumen -->
          <v-col cols="12" md="6" v-if="baseQuote && selectedBlock">
            <v-card outlined class="pa-4 elevation-2" color="grey lighten-5">
              <v-card-title class="pa-0 mb-3">
                <h4 class="primary--text">Resumen de Cuotas</h4>
              </v-card-title>
              <v-card-text class="pa-0">
                <div v-for="(category, index) in quoteSummary" :key="category.name" class="mb-2">
                  <div class="px-2 py-2 rounded" style="background-color: rgba(25, 118, 210, 0.04);">
                    <div class="text-body-2 grey--text text--darken-2 mb-1">{{ category.name }}</div>
                    <v-text-field
                      v-model="quoteSummary[index].quote"
                      type="number"
                      step="0.01"
                      min="0"
                      prefix="$"
                      dense
                      outlined
                      hide-details
                      color="primary"
                      class="quote-input"
                      @input="onQuoteChange(index, $event)"
                    ></v-text-field>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          text
          @click="closeDialog"
        >
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          @click="updateQuotes"
          :disabled="!baseQuote || !selectedBlock || loading"
          :loading="loading"
        >
          Actualizar Cuotas
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'UpdateQuotesDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    userPermissions: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedBlock: null,
      selectedCategory: [],
      selectedStatus: [],
      baseQuote: null,
      loading: false,
      membershipTypesOptions: [
        { text: 'TODOS', value: 'ALL' }
      ],
      blockOptions: [
        { text: 'TODOS', value: 'ALL' },
        { text: 'A', value: 'A' },
        { text: 'B', value: 'B' },
        { text: 'C', value: 'C' },
        { text: 'D', value: 'D' },
        { text: 'E', value: 'E' },
        { text: 'F', value: 'F' },
        { text: 'G', value: 'G' },
        { text: 'H', value: 'H' }
      ],
      categoryOptions: [
        { text: 'TODOS', value: 'ALL' },
        { text: 'Asociados Temporales', value: 'TEMP' },
        { text: 'Asociados Vitalicios', value: 'VITAL' },
        { text: 'Asociados Honorarios Vitalicios', value: 'HONOR_VITAL' },
        { text: 'Reconocimiento por participacion', value: 'RECOGNITION' },
        { text: 'Ejecutivo de Finanzas', value: 'FINANCE_EXEC' },
        { text: 'Ejecutivo de Finanzas / Digital', value: 'FINANCE_DIGITAL_EXEC' },
        { text: 'Investigador', value: 'INVESTIGADOR' },
        { text: 'Socios con Beneficio', value: 'BENEFIT_PARTNER' },
        { text: 'Nueva Generacion', value: 'NEW_GEN' },
        { text: 'Socio a distancia', value: 'REMOTE_PARTNER' }
      ],
      statusOptions: [
        { text: 'TODOS', value: 'ALL' },
        { text: 'Activo', value: 'ACTIVE' },
        { text: 'Inactivo', value: 'INACTIVO' },
        { text: 'Prospecto', value: 'PROSPECTO' },
        { text: 'Aprobado', value: 'APPROVED' },
        { text: 'Expirado', value: 'EXPIRED' },
      ],
      categoryMultipliers: {
        'ALL': 1,
        'TEMP': 0,
        'VITAL': 0,
        'HONOR_VITAL': 0,
        'RECOGNITION': 0,
        'FINANCE_EXEC': 1,
        'FINANCE_DIGITAL_EXEC': 1,
        'INVESTIGADOR': 1,
        'BENEFIT_PARTNER': 0.2,
        'NEW_GEN': 0.8,
        'REMOTE_PARTNER': 1
      },
      quoteSummary: []
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.fetchDistinctMemberships();
      }
    },
    selectedCategory(newVal, oldVal) {
      // Si se selecciona "TODOS", seleccionar todas las otras categorías
      if (newVal.includes('ALL') && !oldVal.includes('ALL')) {
        this.selectedCategory = this.categoryOptions
          .filter(category => category.value !== 'ALL')
          .map(category => category.value);
        this.calculateQuoteSummary();
      }
      // Si se deselecciona alguna categoría individual, quitar "TODOS" si estaba
      else if (oldVal.includes('ALL') && !newVal.includes('ALL')) {
        // No hacer nada, permitir la deselección individual
      }
    },
    selectedStatus(newVal, oldVal) {
      // Si se selecciona "TODOS", seleccionar todos los otros status
      if (newVal.includes('ALL') && !oldVal.includes('ALL')) {
        this.selectedStatus = this.statusOptions
          .filter(status => status.value !== 'ALL')
          .map(status => status.value);
        this.calculateQuoteSummary();
      }
      // Si se deselecciona algún status individual, quitar "TODOS" si estaba
      else if (oldVal.includes('ALL') && !newVal.includes('ALL')) {
        // No hacer nada, permitir la deselección individual
      }
    }
  },
  methods: {
    async fetchDistinctMemberships() {
      try {
        const response = await this.axios.get('/members/distinct-memberships');
        this.membershipTypesOptions = [
          { text: 'TODOS', value: 'ALL' },
          ...response.data.map(membership => ({
            text: membership.name,
            value: membership.id
          }))
        ];
      } catch (error) {
        console.error('Error fetching distinct memberships:', error);
        this.$emit('error', 'Error al cargar tipos de membresía');
      }
    },

    getBlockText(value) {
      const option = this.blockOptions.find(opt => opt.value === value);
      return option ? option.text : value;
    },

    onQuoteChange(index, value) {
      // Convertir a número y validar
      const numericValue = parseFloat(value) || 0;
      this.quoteSummary[index].quote = numericValue;
    },

    calculateQuoteSummary() {
      if (!this.baseQuote || !this.selectedBlock) {
        this.quoteSummary = [];
        return;
      }

      const baseAmount = parseFloat(this.baseQuote);
      
      // Si no hay categorías seleccionadas, no mostrar nada
      if (this.selectedCategory.length === 0) {
        this.quoteSummary = [];
        return;
      }

      // Mostrar solo las categorías seleccionadas (TODOS ya se convierte en categorías individuales)
      this.quoteSummary = this.categoryOptions
        .filter(category => this.selectedCategory.includes(category.value))
        .map(category => ({
          name: category.text,
          value: category.value,
          quote: Math.round(baseAmount * this.categoryMultipliers[category.value])
        }));
    },

    async updateQuotes() {
      if (!this.baseQuote || !this.selectedBlock) {
        this.$emit('error', 'Por favor complete todos los campos requeridos');
        return;
      }

      this.loading = true;

      try {
        const response = await this.axios.post('/members/edit-quotes', {
          block: this.selectedBlock,
          categories: this.selectedCategory.length > 0 ? this.selectedCategory : null,
          statuses: this.selectedStatus.length > 0 ? this.selectedStatus : null,
          baseQuote: parseFloat(this.baseQuote),
          quotesPerCategory: this.quoteSummary.reduce((acc, item) => {
            acc[item.value] = item.quote;
            return acc;
          }, {})
        });

        if (response.status === 200) {
          this.$emit('success', {
            message: `Se actualizaron las cuotas exitosamente`
          });
          this.closeDialog();
        }
      } catch (error) {
        console.error('Error updating quotes:', error);
        this.$emit('error', 'Error al actualizar las cuotas. Intente nuevamente.');
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.dialog = false;
      this.selectedBlock = null;
      this.selectedCategory = [];
      this.selectedStatus = [];
      this.baseQuote = null;
      this.quoteSummary = [];
    }
  }
};
</script>

<style scoped>
</style>